import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Camera, Check, AlertTriangle, Upload, X } from 'lucide-react';
import API_BASE_URL from '../config/api';

interface DetectedAnswer {
  questionNumber: number;
  questionId: number;
  detectedAnswer: number | null;
  detectedLetter: string | null;
  confidence: number;
  cropImageBase64: string | null;
  warning?: string;
  numberOfAnswers: number;  // Nombre de réponses possibles pour cette question
}

interface DetectionStats {
  total: number;
  detected: number;
  notDetected: number;
  detectionRate: number;
  highConfidence: number;
  mediumConfidence: number;
  lowConfidence: number;
  needsReview: number;
  avgConfidence: number;
}

interface Question {
  id: number;
  question_number: number;
  number_of_answers: number;
  part_id?: number | null;
  correct_answer?: number | null;
}

interface Test {
  id: number;
  title: string;
  totalQuestions: number;
  questions?: Question[];
}

interface Student {
  id: number;
  first_name: string;
  last_name: string;
}

const PhotoValidation: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { testId } = useParams<{ testId: string }>();

  const [test, setTest] = useState<Test | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);
  const [newStudentFirstName, setNewStudentFirstName] = useState<string>('');
  const [newStudentLastName, setNewStudentLastName] = useState<string>('');
  const [showNewStudentForm, setShowNewStudentForm] = useState<boolean>(false);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  const [results, setResults] = useState<DetectedAnswer[]>([]);
  const [stats, setStats] = useState<DetectionStats | null>(null);
  const [uploadId, setUploadId] = useState<string | null>(null);

  const [correctedAnswers, setCorrectedAnswers] = useState<{ [questionId: number]: number | null }>({});
  const [saving, setSaving] = useState<boolean>(false);

  // Modal pour afficher l'image en grand
  const [modalImage, setModalImage] = useState<string | null>(null);

  useEffect(() => {
    fetchTestAndStudents();
  }, [testId]);

  // Gestion de la touche Échap pour fermer le modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && modalImage) {
        setModalImage(null);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [modalImage]);

  const fetchTestAndStudents = async (): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
      navigate('/login');
      return;
    }

    try {
      // Récupérer le test
      const testResponse = await fetch(`${API_BASE_URL}/api/tests/${testId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!testResponse.ok) {
        throw new Error(t('photoValidation.errors.fetchTest'));
      }

      const testData = await testResponse.json();
      setTest({
        id: testData.test.id,
        title: testData.test.title,
        totalQuestions: testData.test.total_questions,
        questions: testData.test.questions || []
      });

      // Récupérer les étudiants ayant déjà répondu à ce test
      try {
        const studentsResponse = await fetch(`${API_BASE_URL}/api/tests/${testId}/results`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (studentsResponse.ok) {
          const studentsData = await studentsResponse.json();
          // Extraire la liste unique des étudiants si disponible
          if (studentsData.students && Array.isArray(studentsData.students)) {
            const uniqueStudents = studentsData.students.map((s: any) => ({
              id: s.id,
              first_name: s.first_name,
              last_name: s.last_name
            }));
            setStudents(uniqueStudents);
          }
        }
      } catch (studentsError) {
        console.warn(t('photoValidation.errors.loadStudents'), studentsError);
        // Ne pas bloquer si la récupération des étudiants échoue
        setStudents([]);
      }

    } catch (error) {
      console.error(t('common.error'), error);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);

      // Créer une URL de prévisualisation
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);

      // Réinitialiser les résultats précédents
      setResults([]);
      setStats(null);
      setUploadId(null);
      setCorrectedAnswers({});
    }
  };

  const handleUpload = async (): Promise<void> => {
    if (!selectedFile || !testId) {
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('photo', selectedFile);
      formData.append('testId', testId);

      // Utiliser toujours OpenCV v2
      const endpoint = '/api/photo-ocr/upload-opencv';

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.suggestion
          ? `${errorData.error}\n\n${errorData.suggestion}`
          : errorData.details
          ? `${errorData.error}: ${errorData.details}`
          : errorData.error || t('photoValidation.errors.uploadProcessing');
        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Fusionner les résultats détectés avec toutes les questions du test
      const mergedResults = mergeDetectedWithAllQuestions(data.results);

      setResults(mergedResults);
      setStats(data.stats);
      setUploadId(data.uploadId);

      // Pré-remplir les réponses corrigées avec les détections (sauf si non détectée)
      const initialAnswers: { [questionId: number]: number | null } = {};
      mergedResults.forEach((r: DetectedAnswer) => {
        // Ne présélectionner que si une réponse a été détectée
        initialAnswers[r.questionId] = r.detectedAnswer !== null ? r.detectedAnswer : null;
      });
      setCorrectedAnswers(initialAnswers);

      console.log('✅ Upload et traitement réussi (OpenCV v2):', data.stats);
      console.log(`📋 ${mergedResults.length} questions totales (${data.results.length} détectées, ${mergedResults.length - data.results.length} non trouvées)`);

    } catch (error) {
      console.error('❌ ' + t('photoValidation.errors.uploadProcessing'), error);
    } finally {
      setUploading(false);
    }
  };

  const mergeDetectedWithAllQuestions = (detectedResults: DetectedAnswer[]): DetectedAnswer[] => {
    if (!test || !test.questions) return detectedResults;

    // Créer un Map des résultats détectés par numéro de question
    const detectedMap = new Map<number, DetectedAnswer>();
    detectedResults.forEach(result => {
      detectedMap.set(result.questionNumber, result);
    });

    // Créer un tableau avec toutes les questions du test
    const allResults: DetectedAnswer[] = test.questions.map(question => {
      const detected = detectedMap.get(question.question_number);

      if (detected) {
        // Question détectée - ajouter le nombre de réponses
        return {
          ...detected,
          numberOfAnswers: question.number_of_answers
        };
      } else {
        // Question non détectée - créer une entrée "non trouvé"
        return {
          questionNumber: question.question_number,
          questionId: question.id,
          detectedAnswer: null,
          detectedLetter: null,
          confidence: 0,
          cropImageBase64: null,
          warning: t('photoValidation.warning.questionNotDetected'),
          numberOfAnswers: question.number_of_answers
        };
      }
    });

    return allResults;
  };

  const handleAnswerCorrection = (questionId: number, answer: number | null): void => {
    setCorrectedAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleSave = async (): Promise<void> => {
    if (!selectedStudentId && !showNewStudentForm) {
      return;
    }

    if (showNewStudentForm && (!newStudentFirstName.trim() || !newStudentLastName.trim())) {
      return;
    }

    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }

    setSaving(true);

    try {
      // Préparer les réponses au format attendu
      const answers = Object.entries(correctedAnswers).map(([questionId, answer]) => ({
        questionId: parseInt(questionId),
        answer: answer
      }));

      const body: any = {
        uploadId,
        testId: parseInt(testId!),
        answers
      };

      if (showNewStudentForm) {
        body.firstName = newStudentFirstName.trim();
        body.lastName = newStudentLastName.trim();
      } else {
        body.studentId = selectedStudentId;
      }

      const response = await fetch(`${API_BASE_URL}/api/photo-ocr/validate-and-save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('photoValidation.save.saveError'));
      }

      // Rediriger vers les résultats
      navigate(`/test-results/${testId}`);

    } catch (error) {
      console.error('❌ ' + t('photoValidation.save.saveError'), error);
    } finally {
      setSaving(false);
    }
  };

  const getConfidenceColor = (confidence: number): string => {
    if (confidence === 0) return 'border-gray-400 bg-gray-50';
    if (confidence >= 70) return 'border-green-500 bg-green-50';
    if (confidence >= 50) return 'border-orange-500 bg-orange-50';
    return 'border-red-500 bg-red-50';
  };

  const getConfidenceBadge = (confidence: number): React.ReactElement => {
    if (confidence >= 70) {
      return <span className="text-xs font-semibold text-green-700 bg-green-200 px-2 py-1 rounded">{t('photoValidation.confidence.high')}: {confidence}%</span>;
    }
    if (confidence >= 50) {
      return <span className="text-xs font-semibold text-orange-700 bg-orange-200 px-2 py-1 rounded">{t('photoValidation.confidence.medium')}: {confidence}%</span>;
    }
    if (confidence > 0) {
      return <span className="text-xs font-semibold text-red-700 bg-red-200 px-2 py-1 rounded">{t('photoValidation.confidence.low')}: {confidence}%</span>;
    }
    return <span className="text-xs font-semibold text-gray-700 bg-gray-200 px-2 py-1 rounded">{t('photoValidation.confidence.notDetected')}</span>;
  };

  if (!test) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="text-white text-xl">{t('photoValidation.loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(`/test-results/${testId}`)}
            className="text-white hover:text-gray-200 transition"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">{test.title}</h1>
            <p className="text-blue-200">{t('photoValidation.title')}</p>
          </div>
        </div>

        {/* Sélection étudiant */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">{t('photoValidation.step1.title')}</h2>

          <div className="flex items-center gap-4 mb-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={!showNewStudentForm}
                onChange={() => setShowNewStudentForm(false)}
              />
              <span>{t('photoValidation.step1.existingStudent')}</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={showNewStudentForm}
                onChange={() => setShowNewStudentForm(true)}
              />
              <span>{t('photoValidation.step1.newStudent')}</span>
            </label>
          </div>

          {!showNewStudentForm ? (
            <select
              value={selectedStudentId || ''}
              onChange={(e) => setSelectedStudentId(parseInt(e.target.value))}
              className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
            >
              <option value="">{t('photoValidation.step1.selectStudent')}</option>
              {students.map(student => (
                <option key={student.id} value={student.id}>
                  {student.last_name} {student.first_name}
                </option>
              ))}
            </select>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <input
                type="text"
                value={newStudentLastName}
                onChange={(e) => setNewStudentLastName(e.target.value)}
                placeholder={t('photoValidation.step1.lastNamePlaceholder')}
                className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              />
              <input
                type="text"
                value={newStudentFirstName}
                onChange={(e) => setNewStudentFirstName(e.target.value)}
                placeholder={t('photoValidation.step1.firstNamePlaceholder')}
                className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
              />
            </div>
          )}
        </div>

        {/* Upload photo */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">{t('photoValidation.step2.title')}</h2>

          <div className="flex flex-col items-center gap-4">
            {previewUrl && (
              <div className="relative w-full max-w-md group">
                <div
                  className="cursor-pointer"
                  onClick={() => setModalImage(previewUrl)}
                  title="Cliquer pour agrandir"
                >
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="w-full h-auto rounded-lg border-2 border-gray-300 group-hover:opacity-90 transition"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg flex items-center justify-center transition pointer-events-none">
                    <span className="text-white opacity-0 group-hover:opacity-100 text-sm font-semibold bg-black bg-opacity-50 px-3 py-1 rounded">{t('photoValidation.step2.clickToEnlarge')}</span>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    setPreviewUrl(null);
                    setResults([]);
                  }}
                  className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition z-10"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <div className="flex gap-4">
              <label className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg cursor-pointer transition">
                <Camera className="w-5 h-5" />
                <span>{t('photoValidation.step2.takePhoto')}</span>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>

              <label className="flex items-center gap-2 px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white font-semibold rounded-lg cursor-pointer transition">
                <Upload className="w-5 h-5" />
                <span>{t('photoValidation.step2.chooseFile')}</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </label>
            </div>

            {selectedFile && results.length === 0 && (
              <button
                onClick={handleUpload}
                disabled={uploading}
                className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold transition ${
                  uploading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>{t('photoValidation.step2.processing')}</span>
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    <span>{t('photoValidation.step2.analyzePhoto')}</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Résultats de détection */}
        {results.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-lg font-bold text-gray-800 mb-4">{t('photoValidation.step3.title')}</h2>

            <div className="space-y-4">
              {results.map((result) => (
                <div
                  key={result.questionNumber}
                  className="border-2 border-gray-300 rounded-lg p-4 bg-white"
                >
                  <div className="flex items-start gap-4">
                    {/* Miniature cliquable ou "Non trouvé" */}
                    {result.cropImageBase64 ? (
                      <div
                        className="cursor-pointer hover:opacity-80 transition group relative"
                        onClick={() => setModalImage(result.cropImageBase64)}
                        title={t('photoValidation.step2.clickToEnlarge')}
                      >
                        <img
                          src={result.cropImageBase64}
                          alt={`Q${result.questionNumber}`}
                          className="w-32 h-24 object-contain border border-gray-300 rounded bg-white"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 rounded flex items-center justify-center transition">
                          <span className="text-white opacity-0 group-hover:opacity-100 text-xs font-semibold">{t('photoValidation.step3.enlarge')}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="w-32 h-24 flex items-center justify-center border-2 border-dashed border-gray-400 rounded bg-gray-100">
                        <span className="text-sm font-semibold text-gray-500 text-center">{t('photoValidation.step3.notFound')}</span>
                      </div>
                    )}

                    {/* Informations et correction */}
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-bold text-gray-800">{t('photoValidation.step3.question')} {result.questionNumber}</h3>
                      </div>

                      {result.warning && (
                        <div className="flex items-center gap-2 mb-2 text-orange-700 text-sm">
                          <AlertTriangle className="w-4 h-4" />
                          <span>{result.warning}</span>
                        </div>
                      )}

                      {/* Boutons de réponse */}
                      <div className="flex gap-2">
                        {Array.from({ length: result.numberOfAnswers }, (_, i) => i + 1).map((answer) => {
                          const letter = String.fromCharCode(64 + answer); // A, B, C, D, E, F...
                          const isSelected = correctedAnswers[result.questionId] === answer;

                          return (
                            <button
                              key={answer}
                              onClick={() => handleAnswerCorrection(result.questionId, answer)}
                              className={`w-12 h-12 rounded-lg font-bold transition ${
                                isSelected
                                  ? 'bg-blue-600 text-white ring-2 ring-blue-400 ring-offset-2'
                                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                              }`}
                            >
                              {letter}
                            </button>
                          );
                        })}
                        <button
                          onClick={() => handleAnswerCorrection(result.questionId, null)}
                          className={`px-4 h-12 rounded-lg font-semibold transition ${
                            correctedAnswers[result.questionId] === null
                              ? 'bg-gray-600 text-white ring-2 ring-gray-400 ring-offset-2'
                              : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                          }`}
                        >
                          {t('photoValidation.step3.empty')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Bouton de sauvegarde */}
        {results.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <button
              onClick={handleSave}
              disabled={saving || (!selectedStudentId && !showNewStudentForm)}
              className={`w-full flex items-center justify-center gap-2 px-8 py-4 rounded-lg font-bold text-lg transition ${
                saving || (!selectedStudentId && !showNewStudentForm)
                  ? 'bg-gray-400 cursor-not-allowed text-gray-200'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  <span>{t('photoValidation.save.saving')}</span>
                </>
              ) : (
                <>
                  <Check className="w-6 h-6" />
                  <span>{t('photoValidation.save.saveAllAnswers')}</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>

      {/* Modal d'agrandissement d'image */}
      {modalImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setModalImage(null)}
        >
          {/* Bouton fermer en haut à droite de l'écran */}
          <button
            onClick={() => setModalImage(null)}
            className="fixed top-4 right-4 bg-red-600 hover:bg-red-700 text-white rounded-full p-3 shadow-lg transition z-[60]"
            title={t('photoValidation.modal.close')}
          >
            <X className="w-6 h-6" />
          </button>

          {/* Image agrandie */}
          <img
            src={modalImage}
            alt="Image agrandie"
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default PhotoValidation;
